/**
 * Template 4: Feature Demo Flow
 *
 * Problem-solution narrative with feature highlights.
 * Target: Products that solve specific problems, feature-rich items
 *
 * Two-Phase Animation Timeline (15 seconds @ 60fps = 900 frames):
 *
 * PHASE 1: Problem-Solution Flow (0-7.5s / frames 0-225)
 * - 0.0-2.0s (0-60):    Problem statement scene
 *   - 0.0-0.5s: Emoji fade in
 *   - 0.5-2.0s: Problem text typing
 * - 2.0-2.5s (60-75):   Transition "Introducing..."
 * - 2.5-5.5s (75-165):  Product & features scene
 *   - 2.5-3.5s: Product image reveal
 *   - 3.5-4.5s: Features list
 * - 5.5-7.0s (165-210): Solution/benefit + price
 * - 7.0-7.5s (210-225): Fade out transition
 *
 * PHASE 2: Caption Scene (7.5-15s / frames 225-450)
 * - 7.5-8.0s (225-240):  Scene fade in
 * - 8.0-12.0s (240-360): Caption typewriter
 * - 12.0-15s (360-450):  Hold with swipe-up indicator
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';
import type { FeatureDemoFlowProps, VisualEffectsConfig } from '../types';
import { DEFAULT_VISUAL_EFFECTS, FONT_ID_TO_FAMILY } from '../types';
import { ProductImage } from '../components/ProductImage';
import { AnimatedText, TextAnimationType } from '../components/AnimatedText';
import { CaptionScene } from '../components/CaptionScene';
import { TemplateBackground } from '../components/TemplateBackground';
// Decorative components
import { SparkleCluster } from '../components/Sparkles';
import { ShimmerEffect } from '../components/ShimmerEffect';
import { DiscountBadge } from '../components/DiscountBadge';

export const FeatureDemoFlow: React.FC<FeatureDemoFlowProps> = ({
  productTitle,
  productImage,
  salePrice,
  problemDescription,
  feature1,
  feature2,
  feature3,
  benefitDescription,
  discountCode,
  discountLabel,
  captionText,
  captionColor = '#ffffff',
  captionFont = 'Modern',
  effects: effectsOverride,
  backgroundType = 'aurora',
  backgroundPalette = 'ocean',
  textAnimation = 'blur_fade',
  language,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Merge user effects with defaults
  const effects: VisualEffectsConfig = {
    ...DEFAULT_VISUAL_EFFECTS,
    ...effectsOverride,
  };

  // Resolve font family from font ID (for Phase 1 text components)
  const fontFamily = FONT_ID_TO_FAMILY[captionFont] || 'Inter, sans-serif';

  // Phase transition point
  const PHASE_TRANSITION = fps * 7.5; // Frame 225

  // Phase 1: Scene boundaries (compressed to 7.5s)
  const PROBLEM_END = fps * 2;
  const TRANSITION_START = fps * 2;
  const TRANSITION_END = fps * 2.5;
  const PRODUCT_START = fps * 2.5;
  const PRODUCT_END = fps * 5.5;
  const BENEFIT_START = fps * 5.5;
  const BENEFIT_END = fps * 7.5;

  // Phase 1 fade out
  const phase1Opacity = interpolate(
    frame,
    [PHASE_TRANSITION - fps * 0.5, PHASE_TRANSITION],
    [1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Current scene (only for Phase 1)
  const isProblemScene = frame < PROBLEM_END;
  const isTransitionScene = frame >= TRANSITION_START && frame < TRANSITION_END;
  const isProductScene = frame >= PRODUCT_START && frame < PRODUCT_END;
  const isBenefitScene = frame >= BENEFIT_START && frame < PHASE_TRANSITION;

  // Determine if we're in Phase 1 or Phase 2
  const isPhase1 = frame < PHASE_TRANSITION;
  const isPhase2 = frame >= PHASE_TRANSITION - fps * 0.5;

  // Ocean theme colors for FeatureDemoFlow
  const brandColor = '#0f0f23';
  const accentColor = '#4ECDC4';

  // Phase 1 content
  const phase1Content = (
    <AbsoluteFill style={{ opacity: phase1Opacity }}>
      {/* Scene 1: Problem */}
      {isProblemScene && <ProblemScene description={problemDescription} textAnimation={textAnimation} captionColor={captionColor} />}

      {/* Scene 2: Transition */}
      {isTransitionScene && <TransitionScene startFrame={TRANSITION_START} textAnimation={textAnimation} captionColor={captionColor} />}

      {/* Scene 3: Product & Features */}
      {isProductScene && (
        <ProductScene
          startFrame={PRODUCT_START}
          productTitle={productTitle}
          productImage={productImage}
          feature1={feature1}
          feature2={feature2}
          feature3={feature3}
          effects={effects}
          textAnimation={textAnimation}
          captionColor={captionColor}
        />
      )}

      {/* Scene 4: Benefit */}
      {isBenefitScene && (
        <BenefitScene
          startFrame={BENEFIT_START}
          benefitDescription={benefitDescription}
          salePrice={salePrice}
          effects={effects}
          textAnimation={textAnimation}
          captionColor={captionColor}
          discountCode={discountCode}
          discountLabel={discountLabel}
        />
      )}
    </AbsoluteFill>
  );

  // Phase 2 content
  const phase2Content = captionText ? (
    <CaptionScene
      captionText={captionText}
      captionColor={captionColor}
      captionFont={captionFont}
      textAnimation={textAnimation}
      startFrame={PHASE_TRANSITION}
      gradientPreset="none"
      showSwipeUp={true}
      language={language}
    />
  ) : null;

  // Combined content
  const combinedContent = (
    <>
      {isPhase1 && phase1Content}
      {isPhase2 && phase2Content}
    </>
  );

  return (
    <TemplateBackground
      backgroundType={backgroundType}
      backgroundPalette={backgroundPalette}
      colorTheme={{
        brandColor,
        accentColor,
      }}
      effects={effects}
      fps={fps}
      rippleConfig={{ startFrame: PRODUCT_START }}
    >
      {combinedContent}
    </TemplateBackground>
  );
};

/**
 * Problem Scene Component (compressed)
 */
const ProblemScene: React.FC<{ description: string; textAnimation: TextAnimationType; captionColor: string }> = ({ description, textAnimation, captionColor }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Emoji fade in (faster)
  const emojiOpacity = interpolate(
    frame,
    [0, fps * 0.3],
    [0, 1],
    { extrapolateRight: 'clamp' }
  );
  const emojiScale = spring({
    frame,
    fps,
    from: 0.5,
    to: 1,
    durationInFrames: fps * 0.4,
  });

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 50,
      }}
    >
      {/* Problem Emoji */}
      <div
        style={{
          fontSize: 80,
          opacity: emojiOpacity,
          transform: `scale(${emojiScale})`,
          marginBottom: 30,
        }}
      >
        :(
      </div>

      {/* Problem Description (uses selected text animation) */}
      <div style={{ maxWidth: 550 }}>
        <AnimatedText
          text={description}
          animationType={textAnimation}
          startFrame={fps * 0.5}
          color={captionColor}
          fontSize={32}
          fontWeight={700}
          textAlign="center"
          charsPerSecond={30}
          wordDelay={4}
        />
      </div>
    </AbsoluteFill>
  );
};

/**
 * Transition Scene Component (faster)
 */
const TransitionScene: React.FC<{ startFrame: number; textAnimation: TextAnimationType; captionColor: string }> = ({ startFrame, textAnimation, captionColor }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  const opacity = interpolate(
    relativeFrame,
    [0, fps * 0.15, fps * 0.35, fps * 0.5],
    [0, 1, 1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  const scale = spring({
    frame: relativeFrame,
    fps,
    from: 0.8,
    to: 1,
    durationInFrames: fps * 0.3,
  });

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity,
        transform: `scale(${scale})`,
      }}
    >
      <div style={{ textShadow: '0 0 30px rgba(78, 205, 196, 0.5)' }}>
        <AnimatedText
          text="Introducing..."
          animationType={textAnimation}
          startFrame={startFrame}
          color={captionColor}
          fontSize={46}
          fontWeight={700}
          textAlign="center"
          wordDelay={4}
        />
      </div>
    </AbsoluteFill>
  );
};

/**
 * Product Scene Component (compressed)
 */
const ProductScene: React.FC<{
  startFrame: number;
  productTitle: string;
  productImage: string;
  feature1: string;
  feature2?: string;
  feature3?: string;
  effects: VisualEffectsConfig;
  textAnimation: TextAnimationType;
  captionColor: string;
}> = ({ startFrame, productTitle, productImage, feature1, feature2, feature3, effects, textAnimation, captionColor }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Title fade in (faster)
  const titleOpacity = interpolate(
    relativeFrame,
    [fps * 0.5, fps * 0.8],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Features appear sequentially (faster)
  const features = [feature1, feature2, feature3].filter(Boolean) as string[];
  const FEATURE_START = fps * 1;
  const FEATURE_GAP = fps * 0.5;

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: 40,
        paddingTop: 60,
      }}
    >
      {/* Product Image with Sparkles */}
      <div style={{ position: 'relative', marginBottom: 0 }}>
        <ProductImage
          src={productImage}
          alt={productTitle}
          startFrame={0}
          width={300}
          height={300}
          borderRadius={16}
          shadow="0 15px 50px rgba(78, 205, 196, 0.3)"
          entranceAnimation="fadeScale"
        />

        {/* Sparkles around the product (ocean colors) */}
        {effects.sparkles && (
          <>
            <SparkleCluster
              centerX={50}
              centerY={50}
              radius={40}
              count={12}
              color="rgba(255, 255, 255, 0.9)"
              startFrame={fps * 0.6}
              seed={12121}
            />
            {/* Cyan accent sparkles */}
            <SparkleCluster
              centerX={85}
              centerY={15}
              radius={10}
              count={5}
              color="#4ECDC4"
              startFrame={fps * 1}
              seed={23232}
            />
          </>
        )}
      </div>

      {/* Product Title (uses selected text animation) */}
      <div
        style={{
          opacity: titleOpacity,
          marginTop: 20,
          marginBottom: 20,
          maxWidth: 500,
        }}
      >
        <AnimatedText
          text={productTitle}
          animationType={textAnimation}
          startFrame={startFrame + fps * 0.5}
          color={captionColor}
          fontSize={30}
          fontWeight={700}
          textAlign="center"
          wordDelay={4}
        />
      </div>

      {/* Features List (uses selected text animation) */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {features.map((feature, index) => (
          <FeatureItem
            key={index}
            text={feature}
            startFrame={FEATURE_START + index * FEATURE_GAP}
            relativeFrame={relativeFrame}
            textAnimation={textAnimation}
            captionColor={captionColor}
          />
        ))}
      </div>
    </AbsoluteFill>
  );
};

/**
 * Feature Item Component (uses selected text animation)
 */
const FeatureItem: React.FC<{
  text: string;
  startFrame: number;
  relativeFrame: number;
  textAnimation: TextAnimationType;
  captionColor: string;
}> = ({ text, startFrame, relativeFrame, textAnimation, captionColor }) => {
  const { fps } = useVideoConfig();

  const localFrame = relativeFrame - startFrame;

  const opacity = interpolate(
    localFrame,
    [0, fps * 0.2],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  const translateX = interpolate(
    localFrame,
    [0, fps * 0.2],
    [-20, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  const checkScale = spring({
    frame: localFrame,
    fps,
    from: 0,
    to: 1,
    durationInFrames: fps * 0.2,
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        opacity,
        transform: `translateX(${translateX}px)`,
      }}
    >
      <span
        style={{
          color: '#4ECDC4',
          fontSize: 22,
          transform: `scale(${checkScale})`,
        }}
      >
        ✓
      </span>
      <AnimatedText
        text={text}
        animationType={textAnimation}
        startFrame={startFrame}
        color={captionColor}
        fontSize={18}
        fontWeight={500}
        textAlign="left"
        wordDelay={3}
      />
    </div>
  );
};

/**
 * Benefit Scene Component (compressed)
 */
const BenefitScene: React.FC<{
  startFrame: number;
  benefitDescription: string;
  salePrice: string;
  effects: VisualEffectsConfig;
  textAnimation: TextAnimationType;
  captionColor: string;
  discountCode?: string;
  discountLabel?: string;
}> = ({ startFrame, benefitDescription, salePrice, effects, textAnimation, captionColor, discountCode, discountLabel }) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const relativeFrame = frame - startFrame;

  // Price reveal (faster)
  const priceOpacity = interpolate(
    relativeFrame,
    [fps * 1, fps * 1.3],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );
  const priceScale = spring({
    frame: relativeFrame - fps * 1,
    fps,
    from: 0.8,
    to: 1,
    durationInFrames: fps * 0.3,
  });

  return (
    <AbsoluteFill
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 50,
      }}
    >
      {/* Success Emoji */}
      <div style={{ fontSize: 60, marginBottom: 20 }}>:)</div>

      {/* Benefit Text (uses selected text animation) */}
      <div style={{ maxWidth: 500, marginBottom: 30 }}>
        <AnimatedText
          text={benefitDescription}
          animationType={textAnimation}
          startFrame={startFrame}
          color={captionColor}
          fontSize={28}
          fontWeight={600}
          textAlign="center"
          charsPerSecond={35}
          wordDelay={4}
        />
      </div>

      {/* Price with Shimmer Effect (uses selected text animation) */}
      <div style={{ position: 'relative' }}>
        <div
          style={{
            opacity: priceOpacity,
            transform: `scale(${priceScale})`,
            backgroundColor: '#4ECDC4',
            padding: '14px 45px',
            borderRadius: 50,
            overflow: 'hidden',
          }}
        >
          <AnimatedText
            text={`Only ${salePrice}`}
            animationType={textAnimation}
            startFrame={startFrame + fps * 1}
            color="#0f0f23"
            fontSize={28}
            fontWeight={700}
            textAlign="center"
            wordDelay={4}
          />

          {/* Shimmer effect inside the price button */}
          {effects.priceShimmer && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
                borderRadius: 50,
              }}
            >
              <ShimmerEffect
                width={60}
                color="rgba(255, 255, 255, 0.5)"
                delay={fps * 0.4}
                duration={25}
                repeatCount={2}
                repeatGap={fps * 2}
                angle={-20}
                intensity={0.7}
                startFrame={fps * 1}
                blur={8}
              />
            </div>
          )}
        </div>

        {/* Sparkles around the price button */}
        {effects.sparkles && (
          <SparkleCluster
            centerX={50}
            centerY={50}
            radius={30}
            count={6}
            color="rgba(255, 255, 255, 0.9)"
            startFrame={fps * 1.5}
            seed={34343}
          />
        )}
      </div>

      {/* Discount Badge */}
      <div style={{ marginTop: 20 }}>
        <DiscountBadge
          discountCode={discountCode}
          discountLabel={discountLabel}
          variant="mono"
          showFrom={startFrame + fps * 1.3}
          fontFamily="Inter, sans-serif"
          textAnimation={textAnimation}
        />
      </div>
    </AbsoluteFill>
  );
};
